import { App } from "antd";
import { Key, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useFrontDelTip from "@/hooks/useFrontDelTip";
import { DEFAULT_PAGE_SIZE, TableLayoutRef } from "@/styles/STableLayout";
import { PrintStateEnum } from "@/apis/purchase";
import { Dayjs } from "dayjs";
import {
  PurchasefInStateEnum,
  PurchaseOrderItem,
  PurchaseOrderStateEnum,
} from "@/types/purchase";

// 批量完结默认拉取的订单状态：已下单 + 已完成（筛选清空时回退到此默认值）
export const DEFAULT_BATCH_ORDER_STATES: Array<PurchaseOrderStateEnum> = [
  PurchaseOrderStateEnum.PAYED,
  PurchaseOrderStateEnum.DONE,
];

export interface BatchCompleteFilters {
  printState?: PrintStateEnum;
  // finishState?: PurchasefFnishStateEnum;
  orderState?: PurchaseOrderStateEnum;
  inState?: PurchasefInStateEnum;
  supplierId?: string;
  keyword?: string;
  startTime?: Dayjs | null;
  endTime?: Dayjs | null;
}

const normalizeFilters = (
  filters?: BatchCompleteFilters
): BatchCompleteFilters => ({
  printState: filters?.printState,
  // finishState: filters?.finishState,
  orderState: filters?.orderState,
  inState: filters?.inState,
  supplierId: filters?.supplierId ?? "",
  keyword: filters?.keyword ?? "",
  startTime: filters?.startTime ?? null,
  endTime: filters?.endTime ?? null,
});

const getDefaultFilters = (): BatchCompleteFilters => normalizeFilters({});

export interface BatchCompleteFetchParams extends BatchCompleteFilters {
  offset: number;
  take: number;
  controller?: AbortController;
}

export interface BatchCompleteFetchResult {
  list: PurchaseOrderItem[];
  total: number;
}

interface UseBatchCompleteTableProps {
  fetchData: (
    params: BatchCompleteFetchParams
  ) => Promise<BatchCompleteFetchResult>;
  submitData: (rows: PurchaseOrderItem[]) => Promise<void>;
  onCompleted?: (rows: PurchaseOrderItem[]) => void;
}

const useBatchCompleteTable = ({
  fetchData,
  submitData,
  onCompleted,
}: UseBatchCompleteTableProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();

  const [open, setOpen] = useState(false);
  const [list, setList] = useState<PurchaseOrderItem[]>([]);
  const [total, setTotal] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [filters, setFilters] = useState<BatchCompleteFilters>(
    getDefaultFilters
  );
  const [searchFilters, setSearchFilters] = useState<BatchCompleteFilters>(
    getDefaultFilters
  );
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [selectedRowsMap, setSelectedRowsMap] = useState<
    Record<string, PurchaseOrderItem>
  >({});
  const hasFetchedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);
  const tableLayoutRef = useRef<TableLayoutRef | null>(null);
  const fetchDataRef = useRef(fetchData);
  const submitDataRef = useRef(submitData);
  const onCompletedRef = useRef(onCompleted);

  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  useEffect(() => {
    submitDataRef.current = submitData;
  }, [submitData]);

  useEffect(() => {
    onCompletedRef.current = onCompleted;
  }, [onCompleted]);

  useEffect(() => {
    if (!open) {
      const nextDefaultFilters = getDefaultFilters();
      setFilters(nextDefaultFilters);
      setSearchFilters(nextDefaultFilters);
    }
  }, [
    open,
  ]);

  const { onConfirm: onBatchCompleteConfirm } = useFrontDelTip({
    cb: (rows: PurchaseOrderItem[]) => submitDataRef.current(rows),
    message: t("common.tip"),
    desc: t("purchase.confirmBatchCompletedProduction"),
    rejectOnCancel: true,
  });

  const selectedRows = useMemo(
    () =>
      selectedRowKeys
        .map(key => selectedRowsMap[String(key)])
        .filter(Boolean),
    [selectedRowKeys, selectedRowsMap]
  );

  const clearSelection = () => {
    setSelectedRowKeys([]);
    setSelectedRowsMap({});
  };

  const abortPendingFetch = () => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  };

  const fetchBatchComplete = async (
    offset: number,
    take = tableLayoutRef.current?.pageSize ?? DEFAULT_PAGE_SIZE
  ) => {
    abortPendingFetch();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const currentRequestId = ++requestIdRef.current;

    try {
      setTableLoading(true);
      const result = await fetchDataRef.current({
        offset,
        take,
        ...searchFilters,
        controller,
      });
      if (
        controller.signal.aborted ||
        currentRequestId !== requestIdRef.current
      ) {
        return;
      }
      setList(result.list);
      setTotal(result.total);
    } catch (err) {
      if ((err as { name?: string })?.name === "AbortError") {
        return;
      }
      throw err;
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setTableLoading(false);
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      }
    }
  };

  useEffect(() => {
    if (!open || !hasFetchedRef.current) return;
    fetchBatchComplete(0);
  }, [searchFilters, open]);

  useEffect(
    () => () => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
    },
    []
  );

  return {
    open,
    tableLayoutRef,
    dataSource: list,
    total,
    tableLoading,
    confirmLoading,
    selectedRowKeys,
    filters,
    searchFilters,
    onOpen: () => {
      setOpen(true);
      if (!hasFetchedRef.current) {
        hasFetchedRef.current = true;
      }
    },
    onCancel: () => {
      abortPendingFetch();
      setOpen(false);
      hasFetchedRef.current = false;
      const nextDefaultFilters = getDefaultFilters();
      setFilters(nextDefaultFilters);
      setSearchFilters(nextDefaultFilters);
      clearSelection();
      setList([]);
      setTotal(0);
    },
    onPageChange: (offset: number, pageSize: number) =>
      fetchBatchComplete(offset, pageSize),
    onFiltersChange: (value: BatchCompleteFilters) => {
      setFilters(normalizeFilters(value));
    },
    onSearch: (value?: BatchCompleteFilters) => {
      setSearchFilters(normalizeFilters(value ?? filters));
      clearSelection();
    },
    onClearFilters: () => {
      const nextDefaultFilters = getDefaultFilters();
      setFilters(nextDefaultFilters);
      setSearchFilters(nextDefaultFilters);
      clearSelection();
    },
    onSelectedRowKeysChange: (keys: Key[], rows: PurchaseOrderItem[]) => {
      setSelectedRowKeys(keys);
      setSelectedRowsMap(last => {
        const next = { ...last };
        const currentPageIds = new Set(list.map(item => item.id));
        const nextKeySet = new Set(keys.map(key => String(key)));

        currentPageIds.forEach(id => {
          if (!nextKeySet.has(id)) {
            delete next[id];
          }
        });

        rows.forEach(row => {
          next[row.id] = row;
        });

        return next;
      });
    },
    onConfirm: async () => {

      try {
        setConfirmLoading(true);
        await onBatchCompleteConfirm(selectedRows);
        notification.success({
          placement: "top",
          message: t("order.operationSucc"),
        });
        onCompletedRef.current?.(selectedRows);
        abortPendingFetch();
        setOpen(false);
        hasFetchedRef.current = false;
        const nextDefaultFilters = getDefaultFilters();
        setFilters(nextDefaultFilters);
        setSearchFilters(nextDefaultFilters);
        clearSelection();
        setList([]);
        setTotal(0);
      } catch (err) {
        if ((err as { name?: string })?.name === "AbortError") {
          return;
        }
        const map: Record<number, string> = {
          10086: t("common.selectLeastOne"),
          10010: t("common.networkError"),
          69001: t("common.serverError"),
        };
        const errorCode =
          typeof err === "number"
            ? err
            : typeof err === "string"
              ? Number(err)
              : NaN;

        notification.error({
          placement: "top",
          message: t("order.optErr"),
          description: map[errorCode] ?? t("common.serverError"),
        });
      } finally {
        setConfirmLoading(false);
      }
    },
  };
};

export default useBatchCompleteTable;
